import { z } from 'zod'

export const lotoReportScopeSchema = z.strictObject({
  organization: z.string().min(1),
  site: z.string().min(1),
  pipelineConfig: z.string().min(1),
})

const lotoAttachmentSchema = z.strictObject({
  system_document_id: z.string().min(1),
  page_ids: z.array(z.string().min(1)).min(1),
})

export const lotoIsolationPointSchema = z.object({
  node_id: z.string().min(1),
  page_id: z.string().min(1),
  tag: z.string(),
  description: z.string().default(''),
  isolation_position: z.string().default(''),
})

const lotoTargetEquipmentSchema = z.object({
  node_id: z.string().min(1),
  page_id: z.string().min(1),
  tag: z.string(),
  description: z.string().default(''),
})

function firstTargetPerNode<Target extends { node_id: string }>(
  targets: Target[]
): Target[] {
  return targets.filter(
    (target, index) =>
      targets.findIndex(({ node_id }) => node_id === target.node_id) === index
  )
}

export const lotoReportSchema = z.object({
  name: z.string().trim().min(1).max(500),
  created_at: z.iso.datetime({ offset: true }),
  equipment_system: z.string().default(''),
  loto_index: z.string().default(''),
  requestor: z.string().default(''),
  reason: z.string().default(''),
  issued_by: z.string().default(''),
  scheduled_at: z.iso.datetime({ offset: true }).optional(),
  confined_space_number: z.string().default(''),
  work_order_number: z.string().default(''),
  lock_box_number: z.string().default(''),
  attachments: z.array(lotoAttachmentSchema),
  isolation_points: z.array(lotoIsolationPointSchema),
  target_equipment: z
    .array(lotoTargetEquipmentSchema)
    .default([])
    .transform(firstTargetPerNode),
})

export const lotoReportEntrySchema = z.looseObject({
  id: z.string().min(1),
  type: z.literal('loto-report'),
  tags: z.looseObject({
    sandbox: z.literal('sandbox'),
    organization: z.string().min(1),
    site: z.string().min(1),
    'pipeline-config': z.string().min(1),
  }),
  data: lotoReportSchema,
  updated_at: z.iso.datetime({ offset: true }).nullish(),
  last_updated_by: z.string().nullish(),
})

export type LotoReportScope = z.infer<typeof lotoReportScopeSchema>
export type LotoAttachment = z.infer<typeof lotoAttachmentSchema>
export type LotoIsolationPoint = z.infer<typeof lotoIsolationPointSchema>
export type LotoTargetEquipment = z.infer<typeof lotoTargetEquipmentSchema>
export type LotoReport = z.infer<typeof lotoReportSchema>
export type LotoReportEntry = z.infer<typeof lotoReportEntrySchema>
